/**
 * Who you are, without an account.
 *
 * The site has never had a login and should not grow one: a leaderboard is
 * exactly the feature that would otherwise force a sign up on somebody who
 * wants two minutes of Snake. So identity is an id the SERVER issued, kept in
 * localStorage, plus a display name you can change whenever you like.
 *
 * The id is a bearer credential: whoever holds it can post scores as you. That
 * is an acceptable trade for a family leaderboard and a deliberate one, but it
 * is why the id is never shown in the interface, never put in a URL, and only
 * ever leaves this device through a transfer code you asked for.
 */

import { DEFAULT_LEVEL, asLevel, gameById, type Level } from '@arena/games';

const KEY = 'arena.identity.v1';

export interface Identity {
  id: string;
  name: string;
}

export interface RankRow {
  game: string;
  level: Level;
  value: number;
  rank: number;
}

let current: Identity | null = null;
let ranks = new Map<string, RankRow>();

function read(): Identity | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const { id, name } = parsed as Record<string, unknown>;
    if (typeof id !== 'string' || !id) return null;
    return { id, name: typeof name === 'string' ? name : 'Joueur' };
  } catch {
    // Private browsing, or something else wrote here. Start fresh rather than
    // failing: the games must work even when nothing can be remembered.
    return null;
  }
}

function write(identity: Identity): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(identity));
  } catch {
    // Storage blocked. Play continues, scores just are not kept.
  }
}

export function cachedIdentity(): Identity | null {
  return current ?? read();
}

export function nameOrDefault(): string {
  return cachedIdentity()?.name ?? '';
}

/**
 * Register with the server and learn where we stand.
 *
 * Sends whatever id we already hold. The server keeps it if it issued it, and
 * quietly hands back a new one otherwise, so a corrupted or invented id
 * self heals into a working identity instead of a permanent error.
 */
export async function identify(name?: string): Promise<Identity | null> {
  const stored = read();
  try {
    const res = await fetch('/api/identify', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ id: stored?.id ?? null, name: name ?? stored?.name ?? null }),
    });
    if (!res.ok) return stored;
    const body = (await res.json()) as Identity & { ranks?: RankRow[] };
    current = { id: body.id, name: body.name };
    write(current);
    ranks = new Map((body.ranks ?? []).map((r) => [r.game, r]));
    return current;
  } catch {
    // Offline, or the server is asleep and the wake failed. The arcade is
    // entirely playable without a leaderboard, so this is not an error state.
    current = stored;
    return stored;
  }
}

/**
 * Your standing, which is a fact about a game AND a difficulty.
 *
 * Keyed on both, because they are separate boards. Keying on the game alone
 * would show your hard mode record while you were looking at the easy board.
 */
const rankKey = (game: string, level: Level): string => `${game}:${level}`;

export function rankFor(game: string, level: Level = DEFAULT_LEVEL): RankRow | null {
  return ranks.get(rankKey(game, level)) ?? null;
}

export function setRank(game: string, row: RankRow): void {
  ranks.set(rankKey(game, row.level), row);
}

/**
 * Which difficulty this device last chose for a game.
 *
 * Kept on the device rather than on the identity: it is a comfort setting, it
 * should survive a reload, and it is not worth a round trip before the game can
 * start. A game that offers no difficulties always answers with the default.
 */
const LEVEL_KEY = 'arena.levels';

function levelMap(): Record<string, string> {
  try {
    const raw = localStorage.getItem(LEVEL_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, string>) : {};
  } catch {
    return {};
  }
}

export function chosenLevel(game: string): Level {
  const meta = gameById(game);
  if (meta && !meta.levels) return DEFAULT_LEVEL;
  return asLevel(levelMap()[game]);
}

export function chooseLevel(game: string, level: Level): void {
  try {
    localStorage.setItem(LEVEL_KEY, JSON.stringify({ ...levelMap(), [game]: level }));
  } catch {
    // A device with storage refused still plays; it just forgets the choice.
  }
}

/**
 * Send a solo result.
 *
 * Fire and forget from the caller's point of view: a game must never wait on
 * the network to tell you that you beat your record. Returns the new standing
 * when the server accepted it, or null when it did not, which the caller may
 * use to update a rank but must not depend on.
 */
export async function submitScore(
  game: string,
  value: number,
  level: Level = DEFAULT_LEVEL,
): Promise<{ best: number; improved: boolean; rank: number; rubies: number; earned: number } | null> {
  const me = cachedIdentity();
  if (!me) return null;
  try {
    const res = await fetch('/api/score', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ id: me.id, game, value, level }),
    });
    if (!res.ok) return null;
    const body = (await res.json()) as {
      best: number;
      improved: boolean;
      rank: number;
      rubies: number;
      earned: number;
    };
    setRank(game, { game, level, value: body.best, rank: body.rank });
    return body;
  } catch {
    return null;
  }
}

export interface BoardRow {
  rank: number;
  playerId: string;
  name: string;
  value: number;
  played: number;
}

export async function fetchBoard(
  game: string,
  limit = 20,
  level: Level = DEFAULT_LEVEL,
): Promise<{ rows: BoardRow[]; me: BoardRow | null }> {
  const me = cachedIdentity();
  const query = new URLSearchParams({ game, limit: String(limit), level });
  if (me) query.set('id', me.id);
  try {
    const res = await fetch(`/api/board?${query.toString()}`);
    if (!res.ok) return { rows: [], me: null };
    return (await res.json()) as { rows: BoardRow[]; me: BoardRow | null };
  } catch {
    return { rows: [], me: null };
  }
}

export interface SummaryRow {
  id: string;
  level: Level;
  leader: { name: string; value: number } | null;
  me: { value: number; rank: number } | null;
}

/**
 * Everything the hub needs, in one request rather than twenty two.
 *
 * Difficulty is chosen per game, so this sends the whole set of choices in one
 * compact parameter. A card then shows the leader and your standing on the
 * board you actually play, and says which difficulty that is rather than
 * leaving you to guess.
 */
export async function fetchSummary(): Promise<Map<string, SummaryRow>> {
  const me = cachedIdentity();
  try {
    const query = new URLSearchParams({ level: DEFAULT_LEVEL });
    // What this device chose for each game, so a card shows the board you
    // actually play rather than one you have never touched.
    const chosen = Object.entries(levelMap())
      .map(([game, want]) => `${game}:${asLevel(want)}`)
      .join(',');
    if (chosen) query.set('levels', chosen);
    if (me) query.set('id', me.id);
    const res = await fetch(`/api/summary?${query.toString()}`);
    if (!res.ok) return new Map();
    const body = (await res.json()) as { games: SummaryRow[] };
    return new Map(body.games.map((g) => [g.id, g]));
  } catch {
    return new Map();
  }
}

/** Ask for a code that moves this identity to another device. */
export async function createTransferCode(): Promise<{ code: string; expiresAt: number } | null> {
  const me = cachedIdentity();
  if (!me) return null;
  try {
    const res = await fetch('/api/transfer', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ id: me.id }),
    });
    return res.ok ? ((await res.json()) as { code: string; expiresAt: number }) : null;
  } catch {
    return null;
  }
}

/**
 * Adopt an identity from a transfer code.
 *
 * This REPLACES whoever this device was, which is the point, and is why the
 * interface asks for confirmation before calling it.
 */
export async function claimTransferCode(code: string): Promise<Identity | null> {
  try {
    const res = await fetch('/api/claim', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    if (!res.ok) return null;
    const body = (await res.json()) as Identity & { ranks?: RankRow[] };
    current = { id: body.id, name: body.name };
    write(current);
    ranks = new Map((body.ranks ?? []).map((r) => [r.game, r]));
    return current;
  } catch {
    return null;
  }
}

/**
 * Push any bests recorded before there were leaderboards.
 *
 * The arcade kept personal bests in localStorage from the day it shipped. Those
 * are real results somebody earned, so they are sent once rather than thrown
 * away when the board arrives. The marker means it happens exactly once, even
 * though the old key is deliberately left in place.
 */
const MIGRATED = 'arena.migrated.v1';

export async function migrateLocalBests(): Promise<number> {
  try {
    if (localStorage.getItem(MIGRATED)) return 0;
    const raw = localStorage.getItem('arcade.best.v1');
    localStorage.setItem(MIGRATED, String(Date.now()));
    if (!raw) return 0;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return 0;

    let sent = 0;
    for (const [game, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value !== 'number' || !Number.isFinite(value)) continue;
      // Sent one at a time and awaited: this runs once, and a burst of twenty
      // parallel writes is exactly what the rate limiter is there to stop.
      const result = await submitScore(game, value);
      if (result) sent++;
    }
    return sent;
  } catch {
    return 0;
  }
}


export interface OpenLobby {
  code: string;
  mode: string;
  host: string;
  players: number;
  capacity: number;
  openedAt: number;
}

/**
 * Public lobbies waiting for players at one game.
 *
 * Returns an empty list on any failure rather than throwing. A game page whose
 * "create a lobby" button stops working because the list could not load would
 * be a poor trade for a convenience.
 */
export async function fetchOpenLobbies(game: string): Promise<OpenLobby[]> {
  try {
    const res = await fetch(`/api/lobbies?game=${encodeURIComponent(game)}`);
    if (!res.ok) return [];
    const body = (await res.json()) as { lobbies: OpenLobby[] };
    return Array.isArray(body.lobbies) ? body.lobbies : [];
  } catch {
    return [];
  }
}


export interface Wallet {
  rubies: number;
  skin: string;
  owned: string[];
}

export interface ShopState extends Wallet {
  skins: Array<{ id: string; name: string; cost: number }>;
}

/** The whole shop in one request: catalogue, wallet and what is worn. */
export async function fetchShop(): Promise<ShopState | null> {
  const me = cachedIdentity();
  try {
    const res = await fetch(`/api/skins${me ? `?id=${encodeURIComponent(me.id)}` : ''}`);
    return res.ok ? ((await res.json()) as ShopState) : null;
  } catch {
    return null;
  }
}

/**
 * Buy a skin.
 *
 * The price, the balance and the ownership are all decided by the server; this
 * only reports what it said. A refusal is a normal outcome, not an error.
 */
export async function buySkin(skin: string): Promise<{ ok: boolean; reason?: string } & Wallet | null> {
  const me = cachedIdentity();
  if (!me) return null;
  try {
    const res = await fetch('/api/skins/buy', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ id: me.id, skin }),
    });
    return (await res.json()) as { ok: boolean; reason?: string } & Wallet;
  } catch {
    return null;
  }
}

export async function equipSkin(skin: string): Promise<Wallet | null> {
  const me = cachedIdentity();
  if (!me) return null;
  try {
    const res = await fetch('/api/skins/equip', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ id: me.id, skin }),
    });
    return res.ok ? ((await res.json()) as Wallet) : null;
  } catch {
    return null;
  }
}
